import React, {useCallback, useEffect, useRef} from "react";
import { connect } from "react-redux";
import {withRouter} from "react-router-dom";
import PropTypes from "prop-types";

import {
  setWizardActiveItem,
  setWizardActiveStep,
  setWizardSelectedValues
} from "../../../../../actions/wizardFunctions";
import {getActiveWizardItem, getLookRootUrl, getWizardIdFromUrl} from "./lookWizardFunctions";
import {setLookCompatabilityAlert} from "../../../../../actions/lookFunctions";

import WithUpdateLookCartItem from "../../../hocs/withUpdateLookCartItem";
import LookWizardHooksProvider from "./lookWizardHooksProvider";


const LookWizard = (props) => {

  const {wizardList, location, history, setWizardActiveItem, wizardActiveItem, setWizardActiveStep, setWizardSelectedValues,
    children, lookCompatabilityAlert, setLookCompatabilityAlert, wizardActiveStep, lookOpened} = props;

  // the menu can be closed without changing the url (footer button, canvas click), and closing it
  // resets wizardActiveItem in the wizardReducer. we keep the previous value to detect that
  const menuWasOpenedRef = useRef(lookOpened);

  const closeCompatability = useCallback(() => {
    setLookCompatabilityAlert({
      visible: false,
      compatabilityLogic: [],
      clickedFeature: null,
      applyFunction: null
    })
  }, [setLookCompatabilityAlert])

  useEffect(() => {
    if (!wizardActiveItem) {
      const wizardItem = getActiveWizardItem(wizardList, location);
      if (wizardItem) {
        setWizardActiveItem(wizardItem)
      } else if (Array.isArray(wizardList) && wizardList.length
        && location && getWizardIdFromUrl(location.pathname)) {
        // the wizard from the url does not exist - go back to the look root url
        history.replace(getLookRootUrl(location.pathname));
      }
    }
    return () => {
      setWizardActiveItem(null);
      setWizardActiveStep(-1);
      setWizardSelectedValues({}, true);
      if (lookCompatabilityAlert && lookCompatabilityAlert.visible) {
        closeCompatability()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wizardList])

  useEffect(() => {
    const menuWasOpened = menuWasOpenedRef.current;
    menuWasOpenedRef.current = lookOpened;
    // the wizard lives on its own url, but the menu can be closed without leaving it
    // (footer collapse button, canvas click). the wizardReducer resets wizardActiveItem on close,
    // so staying on the wizard url would leave us with a wizard url and an empty wizard section.
    // leaving the url here keeps both in sync - reopening the menu shows the regular look list
    // and the wizard selections are restored from allWizardsValues when it is opened again.
    // the initial render is skipped on purpose: a direct link to /look-0/wizard/106 is opened
    // with the closed menu and the menu is opened by setting the active item
    if (menuWasOpened && !lookOpened && getWizardIdFromUrl(location.pathname)) {
      history.replace(getLookRootUrl(location.pathname));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookOpened]);

  useEffect(() => {
    if (lookCompatabilityAlert && lookCompatabilityAlert.visible) {
      closeCompatability()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wizardActiveStep, wizardActiveItem]);

  if (!wizardActiveItem) {
    return null
  }

  return (
    <WithUpdateLookCartItem>
      {(updateLookCartItem) => (
        <LookWizardHooksProvider updateLookCartItem={updateLookCartItem}>
          {children}
        </LookWizardHooksProvider>
      )}
    </WithUpdateLookCartItem>
  )
};

LookWizard.propTypes = {
  children: PropTypes.oneOfType([PropTypes.func, PropTypes.node]).isRequired,
}

const mapStateToProps = (store) => {
  const {wizardList, wizardActiveItem, wizardActiveStep} = store.wizardState;
  const {lookCompatabilityAlert, lookOpened} = store.lookState;

  return {
    wizardList,
    wizardActiveItem,
    wizardActiveStep,
    lookCompatabilityAlert,
    lookOpened,
  };
};

const mapDispatchToProps = {
  setWizardActiveItem,
  setWizardActiveStep,
  setWizardSelectedValues,
  setLookCompatabilityAlert,
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(LookWizard)
)
